import { c as _c } from "react/compiler-runtime";
import { deleteOrgFrequentNotesByFrequentNoteIdMutation, getOrgFrequentNotesOptions } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoQuoteLeft16 } from '@onefootprint/icons';
import type { LabelKind, OrgFrequentNote, Review, TenantFrequentNoteKind, TerminalDecisionStatus } from '@onefootprint/request-types/dashboard';
import { Checkbox, Divider, Form, LinkButton, Tooltip } from '@onefootprint/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { DismissableBanner } from '@/components/dismissable-banner';
export type ManualReviewFormData = {
  frequentNote: boolean;
  isPinned: boolean;
  label?: LabelKind;
  note: string;
  reviewsToClear: string[];
};
export type ManualReviewFormProps = {
  frequentNoteKind: TenantFrequentNoteKind;
  frequentNotes: OrgFrequentNote[];
  pendingReviews: Review[];
  status: TerminalDecisionStatus;
};
const ManualReviewForm = t0 => {
  const $ = _c(130);
  const {
    frequentNotes,
    frequentNoteKind,
    pendingReviews,
    status
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "manualReview"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const showRequestErrorToast = useRequestErrorToast();
  const formMethods = useFormContext();
  const {
    control,
    setValue
  } = formMethods;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = deleteOrgFrequentNotesByFrequentNoteIdMutation();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const deleteMutation = useMutation(t2);
  const queryClient = useQueryClient();
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["note", "reviewsToClear"];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== control) {
    t4 = {
      control,
      name: t3
    };
    $[3] = control;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [note, reviewsToClear] = useWatch(t4);
  let t5;
  if ($[5] !== frequentNotes || $[6] !== note) {
    let t6;
    if ($[8] !== note) {
      t6 = t7 => {
        const {
          content
        } = t7;
        return content === note;
      };
      $[8] = note;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t5 = frequentNotes.some(t6);
    $[5] = frequentNotes;
    $[6] = note;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const hasNote = t5;
  let allReviewIds;
  let t6;
  if ($[10] !== pendingReviews || $[11] !== reviewsToClear) {
    allReviewIds = pendingReviews.map(_temp);
    t6 = allReviewIds.length > 0 && allReviewIds.every(id => reviewsToClear.includes(id));
    $[10] = pendingReviews;
    $[11] = reviewsToClear;
    $[12] = allReviewIds;
    $[13] = t6;
  } else {
    allReviewIds = $[12];
    t6 = $[13];
  }
  const allSelected = t6;
  let t7;
  if ($[14] !== reviewsToClear || $[15] !== setValue) {
    t7 = reviewId => {
      const current = reviewsToClear;
      if (current.includes(reviewId)) {
        setValue("reviewsToClear", current.filter(id_0 => id_0 !== reviewId));
      } else {
        setValue("reviewsToClear", [...current, reviewId]);
      }
    };
    $[14] = reviewsToClear;
    $[15] = setValue;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleToggleReview = t7;
  let t8;
  if ($[17] !== allReviewIds || $[18] !== allSelected || $[19] !== setValue) {
    t8 = () => {
      if (allSelected) {
        setValue("reviewsToClear", []);
      } else {
        setValue("reviewsToClear", allReviewIds);
      }
    };
    $[17] = allReviewIds;
    $[18] = allSelected;
    $[19] = setValue;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleToggleAll = t8;
  let t9;
  if ($[21] !== deleteMutation || $[22] !== frequentNoteKind || $[23] !== frequentNotes || $[24] !== queryClient || $[25] !== showRequestErrorToast) {
    t9 = async id_1 => {
      const previousNotes = frequentNotes;
      const newNotes = frequentNotes.filter(note_0 => note_0.id !== id_1);
      queryClient.setQueryData(getOrgFrequentNotesOptions({
        query: {
          kind: frequentNoteKind
        }
      }).queryKey, newNotes);
      ;
      try {
        await deleteMutation.mutateAsync({
          path: {
            frequentNoteId: id_1
          }
        });
      } catch (t10) {
        const error = t10;
        queryClient.setQueryData(getOrgFrequentNotesOptions({
          query: {
            kind: frequentNoteKind
          }
        }).queryKey, previousNotes);
        showRequestErrorToast(error);
      }
    };
    $[21] = deleteMutation;
    $[22] = frequentNoteKind;
    $[23] = frequentNotes;
    $[24] = queryClient;
    $[25] = showRequestErrorToast;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  const handleDelete = t9;
  let t10;
  if ($[27] !== setValue) {
    t10 = note_1 => {
      setValue("note", note_1.content);
    };
    $[27] = setValue;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const handleUse = t10;
  let t11;
  if ($[29] !== status || $[30] !== t) {
    t11 = status === "pass" ? t(_temp3) : t(_temp4);
    $[29] = status;
    $[30] = t;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11) {
    t12 = {
      status: t11
    };
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = status === "pass";
  const t14 = status === "fail";
  let t15;
  if ($[34] !== t13 || $[35] !== t14) {
    t15 = cx({
      "text-success": t13,
      "text-error": t14
    });
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t15) {
    t16 = {
      status: <span className={t15} />
    };
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t12 || $[40] !== t16) {
    t17 = <div className="text-label-2 text-secondary"><Trans ns="entity-details" i18nKey={_temp2} values={t12} components={t16} /></div>;
    $[39] = t12;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== t) {
    t18 = t(_temp5);
    $[42] = t;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t18) {
    t19 = <p className="text-label-2">{t18}</p>;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== t) {
    t20 = t(_temp6);
    $[46] = t;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t20) {
    t21 = <p className="text-body-2 text-secondary">{t20}</p>;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t19 || $[51] !== t21) {
    t22 = <DismissableBanner component="outcome-scoped-notes" variant="info">{t19}{t21}</DismissableBanner>;
    $[50] = t19;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t) {
    t23 = t(_temp7);
    $[53] = t;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t) {
    t24 = t(_temp8);
    $[55] = t;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t) {
    t25 = t(_temp9);
    $[57] = t;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t25) {
    t26 = {
      required: t25
    };
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t23 || $[62] !== t24 || $[63] !== t26) {
    t27 = <Form.Textarea name="note" size="compact" label={t23} placeholder={t24} rules={t26} />;
    $[61] = t23;
    $[62] = t24;
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== t) {
    t28 = t(_temp10);
    $[65] = t;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t28) {
    t29 = <Form.Toggle name="isPinned" label={t28} size="compact" />;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t) {
    t30 = t(_temp11);
    $[69] = t;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t) {
    t31 = t(_temp12);
    $[71] = t;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  if ($[73] !== hasNote || $[74] !== t31) {
    t32 = <Form.Toggle name="frequentNote" label={t31} size="compact" disabled={hasNote} />;
    $[73] = hasNote;
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== hasNote || $[77] !== t30 || $[78] !== t32) {
    t33 = <Tooltip disabled={hasNote} content={t30}>{t32}</Tooltip>;
    $[76] = hasNote;
    $[77] = t30;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== t29 || $[81] !== t33) {
    t34 = <div className="flex flex-col gap-2">{t29}{t33}</div>;
    $[80] = t29;
    $[81] = t33;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  let t35;
  if ($[83] !== t27 || $[84] !== t34) {
    t35 = <div className="flex flex-col gap-4">{t27}{t34}</div>;
    $[83] = t27;
    $[84] = t34;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Divider variant="secondary" />;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== frequentNotes || $[88] !== handleDelete || $[89] !== handleUse || $[90] !== t) {
    t37 = frequentNotes.length > 0 ? <><div className="flex flex-col gap-4"><header className="flex flex-col gap-1"><div className="text-label-2 text-primary">{t(_temp13)}</div><div className="text-body-2 text-tertiary">{t(_temp14)}</div></header><ul className="flex flex-col gap-3">{frequentNotes.map(note_2 => <li key={note_2.id}><div className="flex flex-col gap-4 self-stretch px-4 py-3 rounded border border-solid border-tertiary hover:border-primary bg-primary group"><div className="flex items-center gap-2 self-stretch flex-start"><IcoQuoteLeft16 /><div className="text-body-2 text-primary">{note_2.content}</div></div><div className="flex items-center gap-2"><LinkButton variant="label-2" onClick={() => handleUse(note_2)}>{t(_temp15)}</LinkButton><div className="opacity-0 transition-all duration-200 group-hover:opacity-100 max-md:opacity-100"><div className="flex items-center gap-2"><div className="text-label-2 text-tertiary">·</div><LinkButton variant="label-2" destructive={true} onClick={() => handleDelete(note_2.id)}>{t(_temp16)}</LinkButton></div></div></div></div></li>)}</ul></div><Divider variant="secondary" /></> : null;
    $[87] = frequentNotes;
    $[88] = handleDelete;
    $[89] = handleUse;
    $[90] = t;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  let t38;
  if ($[92] !== allSelected || $[93] !== handleToggleAll || $[94] !== handleToggleReview || $[95] !== pendingReviews || $[96] !== reviewsToClear || $[97] !== t) {
    t38 = pendingReviews.length > 0 ? <><div className="flex flex-col gap-3"><header className="flex flex-col gap-1"><div className="text-label-2 text-primary">{t(_temp17)}</div><div className="text-body-2 text-tertiary">{t(_temp18)}</div></header><div className="flex flex-col gap-2"><Checkbox checked={allSelected} label={t(_temp19)} onChange={handleToggleAll} /><Divider variant="secondary" />{pendingReviews.map(review => <Checkbox key={review.id} checked={reviewsToClear.includes(review.id)} label={review.kind.name} onChange={() => handleToggleReview(review.id)} />)}</div></div><Divider variant="secondary" /></> : null;
    $[92] = allSelected;
    $[93] = handleToggleAll;
    $[94] = handleToggleReview;
    $[95] = pendingReviews;
    $[96] = reviewsToClear;
    $[97] = t;
    $[98] = t38;
  } else {
    t38 = $[98];
  }
  let t39;
  if ($[99] !== t) {
    t39 = t(_temp20);
    $[99] = t;
    $[100] = t39;
  } else {
    t39 = $[100];
  }
  let t40;
  if ($[101] !== t) {
    t40 = t(_temp21);
    $[101] = t;
    $[102] = t40;
  } else {
    t40 = $[102];
  }
  let t41;
  if ($[103] !== t) {
    t41 = t(_temp22);
    $[103] = t;
    $[104] = t41;
  } else {
    t41 = $[104];
  }
  let t42;
  if ($[105] !== t41) {
    t42 = {
      label: t41,
      value: "active"
    };
    $[105] = t41;
    $[106] = t42;
  } else {
    t42 = $[106];
  }
  let t43;
  if ($[107] !== t) {
    t43 = t(_temp23);
    $[107] = t;
    $[108] = t43;
  } else {
    t43 = $[108];
  }
  let t44;
  if ($[109] !== t43) {
    t44 = {
      label: t43,
      value: "offboard_fraud"
    };
    $[109] = t43;
    $[110] = t44;
  } else {
    t44 = $[110];
  }
  let t45;
  if ($[111] !== t) {
    t45 = t(_temp24);
    $[111] = t;
    $[112] = t45;
  } else {
    t45 = $[112];
  }
  let t46;
  if ($[113] !== t45) {
    t46 = {
      label: t45,
      value: "offboard_other"
    };
    $[113] = t45;
    $[114] = t46;
  } else {
    t46 = $[114];
  }
  let t47;
  if ($[115] !== t42 || $[116] !== t44 || $[117] !== t46) {
    t47 = [t42, t44, t46];
    $[115] = t42;
    $[116] = t44;
    $[117] = t46;
    $[118] = t47;
  } else {
    t47 = $[118];
  }
  let t48;
  if ($[119] !== t39 || $[120] !== t40 || $[121] !== t47) {
    t48 = <Form.Select name="label" size="compact" label={t39} placeholder={t40} options={t47} />;
    $[119] = t39;
    $[120] = t40;
    $[121] = t47;
    $[122] = t48;
  } else {
    t48 = $[122];
  }
  let t49;
  if ($[123] !== t17 || $[124] !== t22 || $[125] !== t35 || $[126] !== t37 || $[127] !== t38 || $[128] !== t48) {
    t49 = <div className="flex flex-col gap-6">{t17}{t22}{t35}{t36}{t37}{t38}{t48}</div>;
    $[123] = t17;
    $[124] = t22;
    $[125] = t35;
    $[126] = t37;
    $[127] = t38;
    $[128] = t48;
    $[129] = t49;
  } else {
    t49 = $[129];
  }
  return t49;
};
export default ManualReviewForm;
function _temp(r) {
  return r.id;
}
function _temp2($) {
  return $.manualReview.dialog.form.prompt;
}
function _temp3($_0) {
  return $_0.status.pass;
}
function _temp4($_1) {
  return $_1.status.fail;
}
function _temp5($_2) {
  return $_2.dialog.form.outcomeScopedNotes.main;
}
function _temp6($_3) {
  return $_3.dialog.form.outcomeScopedNotes.secondary;
}
function _temp7($_4) {
  return $_4.dialog.form.note.label;
}
function _temp8($_5) {
  return $_5.dialog.form.note.placeholder;
}
function _temp9($_6) {
  return $_6.dialog.form.note.required;
}
function _temp10($_7) {
  return $_7.dialog.form.pinNote;
}
function _temp11($_8) {
  return $_8.dialog.form.saveAsFrequentNote.tooltip;
}
function _temp12($_9) {
  return $_9.dialog.form.saveAsFrequentNote.label;
}
function _temp13($_10) {
  return $_10.dialog.form.frequentNotes.title;
}
function _temp14($_11) {
  return $_11.dialog.form.frequentNotes.description;
}
function _temp15($_12) {
  return $_12.dialog.form.frequentNotes.use;
}
function _temp16($_13) {
  return $_13.dialog.form.frequentNotes.delete;
}
function _temp17($_14) {
  return $_14.dialog.form.reviewsToClear.title;
}
function _temp18($_15) {
  return $_15.dialog.form.reviewsToClear.description;
}
function _temp19($_16) {
  return $_16.dialog.form.reviewsToClear.selectAll;
}
function _temp20($_17) {
  return $_17.dialog.form.fraudLabel.label;
}
function _temp21($_18) {
  return $_18.dialog.form.fraudLabel.placeholder;
}
function _temp22($_19) {
  return $_19.dialog.form.fraudLabel.options.active;
}
function _temp23($_20) {
  return $_20.dialog.form.fraudLabel.options.offboard_fraud;
}
function _temp24($_21) {
  return $_21.dialog.form.fraudLabel.options.offboard_other;
}